/**
 * TouchControlsOverlay — owns the DOM elements that the `TouchController`
 * binds its pointer listeners to. Only manages structure and CSS classes;
 * actual styling lives in `src/styles/touch-controls.css`.
 *
 * Routing of user actions through the codebase:
 *
 *   buildTouchControls(container) -> TouchControlElements
 *     -> new TouchController(elements)            (binds Pointer Events)
 *        -> sample() -> ShipInput                 (per sim tick)
 *
 * The overlay is appended once at boot and toggled visible/hidden by the
 * control-scheme picker. Auto-hide on touch-idle (2s) is handled here so
 * the controller doesn't have to know about visuals.
 */

import type { TouchControlElements } from "../input/TouchController.js";

const AUTO_HIDE_MS = 2000;

export interface TouchOverlay {
	elements: TouchControlElements;
	root: HTMLElement;
	setVisible(visible: boolean): void;
	dispose(): void;
}

export function buildTouchControls(container: HTMLElement): TouchOverlay {
	const root = document.createElement("div");
	root.id = "touch-controls";
	root.className = "touch-controls touch-controls--idle";
	root.innerHTML = `
		<div class="touch-stick touch-stick--left" data-zone>
			<div class="touch-stick__base"></div>
			<div class="touch-stick__knob" data-knob></div>
			<div class="touch-stick__label">pitch · yaw</div>
		</div>
		<div class="touch-stick touch-stick--right" data-zone>
			<div class="touch-stick__base"></div>
			<div class="touch-stick__knob" data-knob></div>
			<div class="touch-stick__label">roll · throttle</div>
		</div>
		<button type="button" class="touch-button touch-button--thrust" data-thrust>thrust</button>
		<button type="button" class="touch-button touch-button--ping" data-ping aria-label="ping">ping</button>
		<button type="button" class="touch-button touch-button--map" data-map aria-label="map">map</button>
	`;
	container.appendChild(root);

	const leftStickZone = root.querySelector<HTMLElement>(".touch-stick--left[data-zone]")!;
	const rightStickZone = root.querySelector<HTMLElement>(".touch-stick--right[data-zone]")!;
	const leftStickKnob = leftStickZone.querySelector<HTMLElement>("[data-knob]")!;
	const rightStickKnob = rightStickZone.querySelector<HTMLElement>("[data-knob]")!;
	const thrustButton = root.querySelector<HTMLElement>("[data-thrust]")!;
	const pingButton = root.querySelector<HTMLElement>("[data-ping]")!;
	const mapButton = root.querySelector<HTMLElement>("[data-map]")!;

	let idleTimer: ReturnType<typeof setTimeout> | null = null;
	const wakeUp = (): void => {
		root.classList.remove("touch-controls--idle");
		if (idleTimer !== null) clearTimeout(idleTimer);
		idleTimer = setTimeout(() => {
			root.classList.add("touch-controls--idle");
		}, AUTO_HIDE_MS);
	};
	root.addEventListener("pointerdown", wakeUp, true);

	const setVisible = (visible: boolean): void => {
		root.style.display = visible ? "" : "none";
	};

	const dispose = (): void => {
		if (idleTimer !== null) clearTimeout(idleTimer);
		root.removeEventListener("pointerdown", wakeUp, true);
		root.remove();
	};

	return {
		elements: {
			leftStickZone,
			leftStickKnob,
			rightStickZone,
			rightStickKnob,
			thrustButton,
			pingButton,
			mapButton,
		},
		root,
		setVisible,
		dispose,
	};
}
